import {
	BetweenOper,
	CalcType,
	Definition,
	Formula,
	FunctionOper,
	Operand,
	OperandDimension,
	OperandType,
	Operation,
	OperationBlockType,
	OperationType,
	Rule,
	ValueType,
	Variable,
	VariableType
} from 'api';
import { CalcItemStatusEnum, OperandGroupEnum } from '../../utils/AppEnums';
import { generateUniqueKey } from '../../utils/Functions';
import { IFormula, IRule } from './reducers';

export const generateRule = (calcType: CalcType, sequence: number): IRule => {
	return {
		key: generateUniqueKey(), // todo: edit
		id: 0,
		name: 'New rule',
		calcType,
		status: CalcItemStatusEnum.New,
		portfolioId: 0,
		formulae: [],
		variables: [],
		enabled: true,
		sequence: sequence ? sequence + 1 : 1
	};
};

export const mapRuleToInternal = (rule: Rule): IRule => {
	let mappedRule: Rule = { ...rule };
	return {
		...mappedRule,
		key: generateUniqueKey(),
		status: rule.id === 0 ? CalcItemStatusEnum.New : CalcItemStatusEnum.Saved,
		variables: mappedRule.variables ? mappedRule.variables : []
	};
};

export const mapRuleToApi = (rule: IRule): Rule => {
	const { key, status, ...r } = rule;
	rule = { ...rule, formulae: [] };
	return r;
};

export const mapRulesToInternal = (rules: Array<Rule>): Array<IRule> => {
	let mappedRules: Array<IRule> = [];
	rules.forEach((r: Rule) => {
		const mappedRule: IRule = mapRuleToInternal(r);
		mappedRules.push(mappedRule);
	});
	return mappedRules;
};

export const generateVariable = (
	seq: number,
	typeVar: VariableType,
	calcType: CalcType
): Variable => {
	// Variables are sorted and handled via array index for update/delete
	return {
		id: 0,
		name: 'New variable',
		sequence: seq + 1,
		type: typeVar,
		typeValue: ValueType.Number, // default
		dimension: [],
		operandDimension: OperandDimension.Scalar,
		calcTypes: [calcType]
	};
};

export const generateFormula = (sequence: number): Formula => {
	let newFormula: Formula = {
		id: 0,
		name: 'New formula',
		description: '-',
		sequence: sequence ? sequence + 1 : 1,
		operations: []
	};
	return newFormula;
};

export const copyFormula = (sequence: number, formula: Formula): Formula => {
	let newFormula: Formula = { ...formula };
	newFormula.id = 0;
	newFormula.sequence = sequence + 1;
	newFormula.name = newFormula.name + ' (Copy)';
	newFormula.operations = newFormula.operations
		? newFormula.operations.map((d: Definition) => {
				return { ...d, id: 0 };
			})
		: [];
	return newFormula;
};

export const mapFormulaToInternal = (formula: Formula): IFormula => {
	const f: IFormula = {
		...formula,
		status: formula.id === 0 ? CalcItemStatusEnum.New : CalcItemStatusEnum.Saved
	};
	return f;
};

export const mapFormulaToApi = (formula: IFormula): Formula => {
	const { status, ...f }: IFormula = formula;
	let retf: Formula = { ...f };
	retf.operations = f.operations.map((d: Definition) => {
		if (typeof d.operand.filter === 'string' && d.operand.filter.slice(-1) === ';') {
			let df: Definition = { ...d };
			df.operand.filter =
				d.operand.filter.length > 0
					? d.operand.filter.substring(0, d.operand.filter.length - 1)
					: '';
			if (df.operand.operandType === OperandType.Parameter) {
				if (df.operand.formField?.operandType === OperandType.CustomField) {
					df.operand.operandType = OperandType.CustomField;
				}
			}
			return df;
		}
		return d;
	});
	return retf;
};

/**
 * Find operation by name. Default return: calculus operation "None"
 * @param name
 * @param type
 * @returns
 */
export const findOperation = (
	operations: Operation[],
	name: string,
	type?: OperationType
): Operation => {
	let defaultOp: Operation = operations?.filter(
		(o: Operation) => o.type === OperationType.AggregateOperation && o.name === 'None'
	)[0];
	if (type && type === OperationType.AggregateOperation) {
		defaultOp = operations?.filter(
			(o: Operation) => o.type === OperationType.AggregateOperation && o.name === name
		)[0];
	}
	if (type && type === OperationType.LogicalOperation) {
		defaultOp = operations?.filter(
			(o: Operation) => o.type === OperationType.LogicalOperation && o.name === name
		)[0];
	}
	if (type && type === OperationType.FunctionOperation) {
		defaultOp = operations?.filter(
			(o: Operation) => o.type === OperationType.FunctionOperation && o.name === name
		)[0];
	}
	return defaultOp;
};

export const generateFormulaDefinition = (
	seq: number,
	type: OperationBlockType,
	operation: Operation
): Definition => {
	let definition: Definition = {
		id: 0,
		betweenOperation: BetweenOper.Add,
		sequence: seq + 1,
		variableValues: [],
		operand: generateOperand(OperandType.Parameter),
		comparand: undefined,
		comparand2: undefined,
		operation: operation,
		reduction: FunctionOper.Empty
	};

	if (type === OperationBlockType.Logical) {
		definition = {
			...definition,
			comparand: undefined,
			comparand2: undefined,
			andTestFormula: undefined
		};
	}

	return definition;
};

export const generateOperand = (type: OperandType, valueType?: ValueType): Operand => {
	return {
		formField: undefined,
		filter: '',
		value: '',
		valueBool: undefined,
		valueNumber: undefined,
		valueType: valueType ? valueType : ValueType.String,
		operandType: type,
		variable: undefined,
		constant: undefined,
		position: undefined
	};
};

export const generateComparands = (
	group: OperandGroupEnum,
	operandType?: OperandType,
	valueType?: ValueType
): Operand => {
	if (group === OperandGroupEnum.Comparand) {
		return {
			formField: undefined,
			filter: '',
			value: '',
			valueBool: undefined,
			valueNumber: undefined,
			operandType: operandType ? operandType : OperandType.Parameter,
			valueType: valueType ? valueType : ValueType.Number,
			variable: undefined
		};
	} else {
		return {
			formField: undefined,
			filter: '',
			value: '',
			valueBool: undefined,
			valueNumber: undefined,
			operandType: operandType ? operandType : OperandType.Parameter,
			valueType: valueType ? valueType : ValueType.Number,
			variable: undefined
		};
	}
};

export const getOperandDimension = (operand: Operand): OperandDimension | undefined => {
	switch (operand.operandType) {
		case OperandType.Constant:
			return operand.constant?.operandDimension;
		case OperandType.Variable:
			return operand.variable?.operandDimension;
		case OperandType.CustomField:
		case OperandType.Parameter:
			return operand.formField?.operandDimension;
		default:
			return OperandDimension.Scalar;
	}
};

export const getOperandDimensionValueType = (operand: Operand): ValueType | undefined => {
	const operandDim = getOperandDimension(operand);
	const operandType = operand.operandType;

	if (operandType === OperandType.Parameter || operandType === OperandType.CustomField) return operand.formField?.valueType;
	if (operandType === OperandType.Constant) {
		switch (operandDim) {
			case OperandDimension.Vector:
				return operand.constant?.vector?.valueType;
			case OperandDimension.Tensor:
				return operand.constant?.tensor?.valueType;
			case OperandDimension.Matrix:
				return;
			case OperandDimension.Scalar:
			default:
				return operand.constant?.value?.valueType;
		}
	}
	if (operandType === OperandType.Variable) return operand.variable?.typeValue;
	if (operandType === OperandType.Value) return operand.variable?.typeValue;
};
